import { MouseEvent, useEffect, useState } from 'react';
import { BrowserRouter as Router, Navigate, Route } from 'react-router';
import { Routes } from '@datadog/browser-rum-react/react-router-v7';
import axios from 'axios';

import { AccountManagement } from '../accounts/AccountManagement';
import { GroupsManagementPage } from '../accounts/GroupsManagement/GroupsManagement';
import { Activations } from '../activations/Activations';
import { BannerCreatePage } from '../banners/BannerCreatePage/BannerCreatePage';
import { Banners } from '../banners/Banners/Banners';
import { BannerUpdatePage } from '../banners/BannerUpdatePage/BannerUpdatePage';
import Carousel from '../carousels/Carousel';
import Carousels from '../carousels/Carousels';
import { EmailAuditItem } from '../emailAudit/EmailAuditItem';
import { EmailAuditList } from '../emailAudit/EmailAuditList';
import { Events } from '../events/Events';
import Gift from '../gifts/Gift';
import Gifts from '../gifts/Gifts';
import { GiftLibraryPage } from '../giftsv2/pages/GiftLibraryPage';
import { GiftPlannerPage } from '../giftsv2/pages/GiftPlannerPage';
import { GiftSchedulerPage } from '../giftsv2/pages/GiftSchedulerPage';
import { GiftsV2Layout } from '../giftsv2/pages/GiftsV2Layout';
import { ApplePriceAutomationPage } from '../iapPriceAutomation/ApplePriceAutomationPage';
import { GooglePriceAutomationPage } from '../iapPriceAutomation/GooglePriceAutomationPage';
import { SkuManagementPage } from '../iapPriceAutomation/SkuManagement/SkuManagementPage';
import InAppEvent from '../inappevents/InAppEvent';
import InAppEvents from '../inappevents/InAppEvents';
import Map from '../maps/Map';
import Maps from '../maps/Maps';
import Medias from '../media/Medias';
import Pack from '../packs/Pack';
import Packs from '../packs/Packs';
import { Portal } from '../portal/Portal';
import { Post } from '../portal/Post';
import { IconLoader } from '../shared/Icon';
import LanguageSelector from '../shared/LanguageSelector';
import Loading from '../shared/Loading';
import { ProtectedComponent } from '../shared/ProtectedComponent';
import ShopTab from '../shopTabs/ShopTab/ShopTab';
import ShopTabs from '../shopTabs/ShopTabs';
import { TagCreatePage } from '../tags/TagCreatePage/TagCreatePage';
import { Tags } from '../tags/Tags/Tags';
import { TagUpdatePage } from '../tags/TagUpdatePage/TagUpdatePage';
import Tools from '../tools/Tools';
import { User } from '../user/User';
import { UserManagement } from '../users/UserManagement/UserManagement.tsx';
import Versions from '../versions/Versions';
import {
    CurrentEnvironmentContext,
    OpenSectionsContext,
    UserContext,
} from './Contexts';
import { FallbackPage } from './FallbackPage';
import Login from './Login/Login';
import Nav, { getTabByRoute, NavigationTab } from './Nav/Nav';
import { UserRoles } from '@lifeserver/shared';

import { useEnabledFeatures } from '../hooks/features';
import { useSelectedLanguage } from '../hooks/selectedLanugage';

import { getCurrentEnv } from '../utils/envaccess.ts';
import { Features } from '../utils/features';
import { getUserProfileData } from '../utils/getUserProfileData';
import { redirectToLogin } from '../utils/redirectToLogin';

import { getRouteByUrl, RoutePath } from '../routes';

import { EnvironmentNameType } from '../types/enums/envnametype.enum';

import type { UserProfilePublic } from '../user/userTypes';

function App() {
    const [isLoading, setIsLoading] = useState(true);
    const { enabledFeatures, isPending: isEnabledFeaturesPending } =
        useEnabledFeatures();
    const isEventsEnabled = Boolean(enabledFeatures?.includes(Features.events));
    const [tab, setTab] = useState<NavigationTab | undefined>(undefined);
    const [openSections, setOpenSections] = useState<string[]>([
        'packOverview',
    ]);
    const [user, setUser] = useState<UserProfilePublic | null>(null);
    const isAuthenticated = Boolean(user);
    const [currentEnvName, setCurrentEnvName] = useState(
        EnvironmentNameType.Production,
    );
    const { selectedLanguage } = useSelectedLanguage();

    async function fetchUserData() {
        const user = await getUserProfileData();
        setUser(user);
    }

    async function updateEnv() {
        const env = await getCurrentEnv();

        if (env) {
            setCurrentEnvName(env);
        }
    }

    useEffect(() => {
        // eslint-disable-next-line react-hooks/set-state-in-effect
        fetchUserData();
        updateEnv();
        setIsLoading(false);

        localStorage.setItem('hasUnsavedChanges', String(false));
    }, []);

    const signout = async (e: MouseEvent<HTMLElement>) => {
        if (!onCheckUnsaved(e)) return;

        await axios.get('/api/v2/auth/logout');

        redirectToLogin();
    };

    const toggleSection = (name: string) => {
        if (openSections.includes(name)) {
            setOpenSections(openSections.filter((section) => section !== name));
        } else {
            setOpenSections([...openSections, name]);
        }
    };

    const onCheckUnsaved = (e: MouseEvent<HTMLElement>) => {
        const hasUnsaved = localStorage.getItem('hasUnsavedChanges') === 'true';
        if (!hasUnsaved) return true;
        if (!window.confirm('Really leave? You have unsaved changes.')) {
            e.preventDefault();
            return false;
        }
        return true;
    };

    const onTabChange = (
        e: MouseEvent<HTMLAnchorElement>,
        tab: NavigationTab,
    ) => {
        if (onCheckUnsaved(e)) {
            setTab(tab);
            localStorage.setItem('hasUnsavedChanges', String(false));
        }
    };

    const getActiveTab = () => {
        if (tab) {
            return tab;
        }

        const activeRouteName = getRouteByUrl(window.location.pathname);

        if (activeRouteName) {
            return getTabByRoute(activeRouteName);
        }

        return undefined;
    };

    if (isLoading || isEnabledFeaturesPending) return <Loading />;

    if (!isAuthenticated)
        return (
            <CurrentEnvironmentContext.Provider value={{ currentEnvName }}>
                <div data-env={currentEnvName}>
                    <Login />
                </div>
            </CurrentEnvironmentContext.Provider>
        );

    const allowedOrigins = [
        'http://localhost:*',
        'ws://localhost:*',
        'ws://*:9876',
        'https://lifeserver-admin.tocaboca.com',
        'https://lifeserver-admin-staging.tocaboca.com',
        'https://lifeserver-admin-dev-growth.tocaboca.com',
        'https://lifeserver-admin-dev.tocaboca.com',
        'https://s3.amazonaws.com',
        'https://s3-eu-west-1.amazonaws.com',
        'https://*.cloudfront.net',
        'https://*.firebaseio.com',
        'https://*.googleapis.com',
        'https://*.gstatic.com',
        'https://browser-intake-datadoghq.eu',
    ];

    const contentSecurityPolicy = `connect-src ${allowedOrigins.join(' ')}`;

    return (
        <UserContext.Provider value={user}>
            <>
                <IconLoader />
                <meta
                    httpEquiv="Content-Security-Policy"
                    content={contentSecurityPolicy}
                />
                <title>
                    {`${
                        Object.keys(EnvironmentNameType)[
                            Object.values(EnvironmentNameType).indexOf(
                                currentEnvName,
                            )
                        ]
                    } TBW CMS`}
                </title>
                <OpenSectionsContext.Provider
                    value={{ openSections, toggleSection }}
                >
                    <Router>
                        <div data-env={currentEnvName}>
                            <CurrentEnvironmentContext.Provider
                                value={{ currentEnvName }}
                            >
                                <Nav
                                    tab={getActiveTab()}
                                    isEventsEnabled={isEventsEnabled}
                                    isUserManagementEnabled={Boolean(
                                        user?.roles.includes(
                                            UserRoles.UserAdmin,
                                        ),
                                    )}
                                    onTabChange={onTabChange}
                                >
                                    <LanguageSelector />

                                    {user && (
                                        <User user={user} signout={signout} />
                                    )}
                                </Nav>
                            </CurrentEnvironmentContext.Provider>
                            <div className="blurrable">
                                <Routes>
                                    <Route
                                        path={RoutePath.Packs}
                                        element={
                                            <ProtectedComponent
                                                role={UserRoles.ShopRead}
                                            >
                                                <Packs
                                                    language={selectedLanguage}
                                                />
                                            </ProtectedComponent>
                                        }
                                    />
                                    <Route
                                        path={RoutePath.PackNew}
                                        element={
                                            <ProtectedComponent
                                                role={UserRoles.ShopWrite}
                                            >
                                                <Pack
                                                    language={selectedLanguage}
                                                />
                                            </ProtectedComponent>
                                        }
                                    />
                                    <Route
                                        path={RoutePath.PackEdit}
                                        element={
                                            <ProtectedComponent
                                                role={UserRoles.ShopRead}
                                            >
                                                <Pack
                                                    language={selectedLanguage}
                                                />
                                            </ProtectedComponent>
                                        }
                                    />
                                    <Route
                                        path={RoutePath.PortalLibrary}
                                        element={
                                            <ProtectedComponent
                                                role={UserRoles.PortalReadPost}
                                            >
                                                <Portal view="library" />
                                            </ProtectedComponent>
                                        }
                                    />
                                    <Route
                                        path={RoutePath.PortalPublication}
                                        element={
                                            <ProtectedComponent
                                                role={
                                                    UserRoles.PortalReadSchedule
                                                }
                                            >
                                                <Portal view="publication" />
                                            </ProtectedComponent>
                                        }
                                    />
                                    <Route
                                        path={RoutePath.PortalPolls}
                                        element={
                                            <ProtectedComponent
                                                role={UserRoles.PortalReadPost}
                                            >
                                                <Portal view="polls" />
                                            </ProtectedComponent>
                                        }
                                    />
                                    <Route
                                        path={RoutePath.PortalSettings}
                                        element={
                                            <ProtectedComponent
                                                role={
                                                    UserRoles.PortalReadSchedule
                                                }
                                            >
                                                <Portal view="settings" />
                                            </ProtectedComponent>
                                        }
                                    />
                                    <Route
                                        path={RoutePath.PortalPostNew}
                                        element={
                                            <ProtectedComponent
                                                role={UserRoles.PortalWritePost}
                                            >
                                                <Post />
                                            </ProtectedComponent>
                                        }
                                    />
                                    <Route
                                        path={RoutePath.PortalPostEdit}
                                        element={
                                            <ProtectedComponent
                                                role={UserRoles.PortalReadPost}
                                            >
                                                <Post />
                                            </ProtectedComponent>
                                        }
                                    />
                                    <Route
                                        path={RoutePath.PortalTranslations}
                                        element={
                                            <ProtectedComponent
                                                role={UserRoles.TranslationRead}
                                            >
                                                <Portal view="translations" />
                                            </ProtectedComponent>
                                        }
                                    />
                                    <Route
                                        path={RoutePath.Gifts}
                                        element={
                                            <ProtectedComponent
                                                role={UserRoles.GiftRead}
                                            >
                                                <Gifts />
                                            </ProtectedComponent>
                                        }
                                    />
                                    <Route
                                        path={RoutePath.GiftNew}
                                        element={
                                            <ProtectedComponent
                                                role={UserRoles.GiftWrite}
                                            >
                                                <Gift />
                                            </ProtectedComponent>
                                        }
                                    />
                                    <Route
                                        path={RoutePath.GiftEdit}
                                        element={
                                            <ProtectedComponent
                                                role={UserRoles.GiftRead}
                                            >
                                                <Gift />
                                            </ProtectedComponent>
                                        }
                                    />
                                    <Route
                                        path={RoutePath.GiftsV2}
                                        element={
                                            <ProtectedComponent
                                                role={UserRoles.GiftRead}
                                            >
                                                <GiftsV2Layout />
                                            </ProtectedComponent>
                                        }
                                    >
                                        <Route
                                            index
                                            element={
                                                <Navigate
                                                    replace
                                                    to={
                                                        RoutePath.GiftsV2Scheduler
                                                    }
                                                />
                                            }
                                        />
                                        <Route
                                            path={RoutePath.GiftsV2Planner}
                                            element={<GiftPlannerPage />}
                                        />
                                        <Route
                                            path={RoutePath.GiftsV2Scheduler}
                                            element={<GiftSchedulerPage />}
                                        />
                                        <Route
                                            path={RoutePath.GiftsV2Library}
                                            element={<GiftLibraryPage />}
                                        />
                                    </Route>
                                    <Route
                                        path={RoutePath.Maps}
                                        element={
                                            <ProtectedComponent
                                                role={UserRoles.ShopRead}
                                            >
                                                <Maps />
                                            </ProtectedComponent>
                                        }
                                    />
                                    <Route
                                        path={RoutePath.MapEdit}
                                        element={
                                            <ProtectedComponent
                                                role={UserRoles.ShopRead}
                                            >
                                                <Map
                                                    language={selectedLanguage}
                                                />
                                            </ProtectedComponent>
                                        }
                                    />
                                    <Route
                                        path={RoutePath.Tabs}
                                        element={
                                            <ProtectedComponent
                                                role={UserRoles.ShopRead}
                                            >
                                                <ShopTabs
                                                    language={selectedLanguage}
                                                />
                                            </ProtectedComponent>
                                        }
                                    />
                                    <Route
                                        path={RoutePath.TabNew}
                                        element={
                                            <ProtectedComponent
                                                role={UserRoles.ShopWrite}
                                            >
                                                <ShopTab
                                                    language={selectedLanguage}
                                                />
                                            </ProtectedComponent>
                                        }
                                    />
                                    <Route
                                        path={RoutePath.TabEdit}
                                        element={
                                            <ProtectedComponent
                                                role={UserRoles.ShopRead}
                                            >
                                                <ShopTab
                                                    language={selectedLanguage}
                                                />
                                            </ProtectedComponent>
                                        }
                                    />
                                    <Route
                                        path={RoutePath.AccountManagement}
                                        element={
                                            <ProtectedComponent
                                                role={UserRoles.AccountsRead}
                                            >
                                                <AccountManagement />
                                            </ProtectedComponent>
                                        }
                                    />
                                    <Route
                                        path={RoutePath.GroupedProfiles}
                                        element={
                                            <ProtectedComponent
                                                role={UserRoles.AccountsRead}
                                            >
                                                <GroupsManagementPage />
                                            </ProtectedComponent>
                                        }
                                    />
                                    <Route
                                        path={RoutePath.Versions}
                                        element={
                                            <ProtectedComponent
                                                role={
                                                    UserRoles.ReleaseManagementRead
                                                }
                                            >
                                                <Versions />
                                            </ProtectedComponent>
                                        }
                                    />
                                    <Route
                                        path={RoutePath.Carousels}
                                        element={
                                            <ProtectedComponent
                                                role={UserRoles.ShopRead}
                                            >
                                                <Carousels />
                                            </ProtectedComponent>
                                        }
                                    />
                                    <Route
                                        path={RoutePath.CarouselNew}
                                        element={
                                            <ProtectedComponent
                                                role={UserRoles.ShopWrite}
                                            >
                                                <Carousel
                                                    language={selectedLanguage}
                                                />
                                            </ProtectedComponent>
                                        }
                                    />
                                    <Route
                                        path={RoutePath.CarouselEdit}
                                        element={
                                            <ProtectedComponent
                                                role={UserRoles.ShopRead}
                                            >
                                                <Carousel
                                                    language={selectedLanguage}
                                                />
                                            </ProtectedComponent>
                                        }
                                    />
                                    <Route
                                        path={RoutePath.Medias}
                                        element={
                                            <ProtectedComponent
                                                role={UserRoles.MediaReadWrite}
                                            >
                                                <Medias />
                                            </ProtectedComponent>
                                        }
                                    />
                                    <Route
                                        path={RoutePath.Tags}
                                        element={
                                            <ProtectedComponent
                                                role={UserRoles.ShopRead}
                                            >
                                                <Tags />
                                            </ProtectedComponent>
                                        }
                                    />
                                    <Route
                                        path={RoutePath.TagNew}
                                        element={
                                            <ProtectedComponent
                                                role={UserRoles.ShopWrite}
                                            >
                                                <TagCreatePage
                                                    language={selectedLanguage}
                                                />
                                            </ProtectedComponent>
                                        }
                                    />
                                    <Route
                                        path={RoutePath.TagEdit}
                                        element={
                                            <ProtectedComponent
                                                role={UserRoles.ShopRead}
                                            >
                                                <TagUpdatePage
                                                    language={selectedLanguage}
                                                />
                                            </ProtectedComponent>
                                        }
                                    />
                                    <Route
                                        path={RoutePath.Banners}
                                        element={
                                            <ProtectedComponent
                                                role={UserRoles.BannersRead}
                                            >
                                                <Banners />
                                            </ProtectedComponent>
                                        }
                                    />
                                    <Route
                                        path={RoutePath.BannerNew}
                                        element={
                                            <ProtectedComponent
                                                role={UserRoles.BannersWrite}
                                            >
                                                <BannerCreatePage
                                                    language={selectedLanguage}
                                                />
                                            </ProtectedComponent>
                                        }
                                    />
                                    <Route
                                        path={RoutePath.BannerEdit}
                                        element={
                                            <ProtectedComponent
                                                role={UserRoles.BannersRead}
                                            >
                                                <BannerUpdatePage
                                                    language={selectedLanguage}
                                                />
                                            </ProtectedComponent>
                                        }
                                    />
                                    <Route
                                        path={RoutePath.EmailAuditList}
                                        element={<EmailAuditList />}
                                    />
                                    <Route
                                        path={RoutePath.EmailAuditItem}
                                        element={<EmailAuditItem />}
                                    />
                                    {isEventsEnabled && (
                                        <Route
                                            path={RoutePath.Events}
                                            element={<Events />}
                                        />
                                    )}
                                    <Route
                                        path={RoutePath.PriceAutomationApple}
                                        element={
                                            <ProtectedComponent
                                                role={
                                                    UserRoles.PriceAutomationRead
                                                }
                                            >
                                                <ApplePriceAutomationPage />
                                            </ProtectedComponent>
                                        }
                                    />
                                    <Route
                                        path={RoutePath.PriceAutomationGoogle}
                                        element={
                                            <ProtectedComponent
                                                role={
                                                    UserRoles.PriceAutomationRead
                                                }
                                            >
                                                <GooglePriceAutomationPage />
                                            </ProtectedComponent>
                                        }
                                    />
                                    <Route
                                        path={RoutePath.SKUManagement}
                                        element={
                                            <ProtectedComponent
                                                role={
                                                    UserRoles.PriceAutomationRead
                                                }
                                            >
                                                <SkuManagementPage />
                                            </ProtectedComponent>
                                        }
                                    />
                                    <Route
                                        path={RoutePath.Tools}
                                        element={
                                            <ProtectedComponent
                                                role={UserRoles.ShopRead}
                                            >
                                                <Tools />
                                            </ProtectedComponent>
                                        }
                                    />
                                    <Route
                                        path={RoutePath.Activations}
                                        element={<Activations />}
                                    />
                                    <Route
                                        path={RoutePath.UserManagement}
                                        element={
                                            <ProtectedComponent
                                                role={UserRoles.UserAdmin}
                                            >
                                                <UserManagement />
                                            </ProtectedComponent>
                                        }
                                    />
                                    <Route
                                        path={RoutePath.InAppEvents}
                                        element={
                                            <ProtectedComponent
                                                role={UserRoles.InAppEventsRead}
                                            >
                                                <InAppEvents
                                                    language={selectedLanguage}
                                                />
                                            </ProtectedComponent>
                                        }
                                    />
                                    <Route
                                        path={RoutePath.InAppEventsEdit}
                                        element={
                                            <ProtectedComponent
                                                role={UserRoles.InAppEventsRead}
                                            >
                                                <InAppEvent
                                                    language={selectedLanguage}
                                                />
                                            </ProtectedComponent>
                                        }
                                    />
                                    <Route
                                        path={RoutePath.InAppEventsNew}
                                        element={
                                            <ProtectedComponent
                                                role={
                                                    UserRoles.InAppEventsWrite
                                                }
                                            >
                                                <InAppEvent
                                                    language={selectedLanguage}
                                                />
                                            </ProtectedComponent>
                                        }
                                    />
                                    <Route
                                        path="*"
                                        element={<FallbackPage />}
                                    />
                                </Routes>
                            </div>
                        </div>
                    </Router>
                </OpenSectionsContext.Provider>
            </>
        </UserContext.Provider>
    );
}

export default App;
